import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Procurement } from '@/types/database.types';

const getQualityInspectionProcurement = async (id: string): Promise<Procurement | null> => {
  // Fetch base procurement with related data
  const { data, error } = await supabase
    .from('procurements')
    .select(`
      *,
      procurement_costs(*),
      procurement_finance(*),
      procurement_quality(*),
      items:procurement_items(
        id,
        product_name,
        quantity,
        price_per_unit,
        quality_grade,
        notes,
        item_quality:procurement_item_quality(
          id,
          quality_grade,
          damage_kg,
          compensation_kg,
          damage_percentage,
          preliminary_damage_kg,
          preliminary_compensation_kg,
          preliminary_damage_percentage,
          grade_notes,
          is_rejected
        )
      )
    `)
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching quality inspection procurement:', error);
    throw error;
  }

  if (!data) return null;

  // Fetch source name based on source_type (polymorphic lookup)
  let source_name = null;
  if (data.source_type === 'supplier' && data.source_id) {
    const { data: supplierData } = await supabase
      .from('suppliers')
      .select('name')
      .eq('id', data.source_id)
      .maybeSingle();
    source_name = supplierData?.name;
  } else if (data.source_type === 'agent' && data.source_id) {
    const { data: agentData } = await supabase
      .from('agents')
      .select('name')
      .eq('id', data.source_id)
      .maybeSingle();
    source_name = agentData?.name;
  }

  // Transform and return the data - use 'as any' to handle the nested item_quality structure
  const procurement = {
    ...data,
    costs: data.procurement_costs,
    finance: data.procurement_finance,
    quality: data.procurement_quality,
    items: data.items,
    source_name
  } as unknown as Procurement;

  return procurement;
};

export const useGetQualityInspectionProcurement = (id: string) => {
  return useQuery({
    queryKey: ['quality-inspection-procurement', id],
    queryFn: () => getQualityInspectionProcurement(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
};
